import {
  createChargeSetting,
  deleteChargeSetting,
  updateChargeSetting,
} from 'graphql/charge-settings/mutations';
import { getChargeSettings } from 'graphql/charge-settings/queries';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import useAlert from 'utils/alert';
import { ModalData } from './types';

import { orderSettingsByName } from './utils';

const getMutationErrorMessage = (errorResponse: any, fallback: string) => (
  errorResponse?.response?.errors?.[0]?.message ?? errorResponse?.message ?? fallback
);

export const useChargeSettingsHooks = () => {
  const showAlert = useAlert();
  const queryCache = useQueryClient();

  const [modalData, setModalData] = useState<ModalData>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const { data, isLoading } = useQuery(['charge-settings'], {
    queryFn: getChargeSettings,
    refetchOnWindowFocus: false,
  });

  const settings = useMemo(
    () => (data ? orderSettingsByName(data.getChargeSettings) : []),
    [data]
  );

  const totalRows = settings.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

  const settingsPages = useMemo(() => (
    Array.from({ length: totalPages }, (_, index) => {
      const start = index * rowsPerPage;
      return settings.slice(start, start + rowsPerPage);
    })
  ), [settings, rowsPerPage, totalPages]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const changeRowsPerPage = ({ rows }: { rows: number }) => {
    setRowsPerPage(rows);
  };

  const onCloseModal = () => {
    setModalData({ modal: '' });
  };

  const { mutate: deleteSettingMutation, isLoading: isDeleteLoading } = useMutation(
    deleteChargeSetting,
    {
      onSuccess: () => {
        onCloseModal();
        queryCache.invalidateQueries(['charge-settings']);
        showAlert('Charge deleted', 'success');
      },
    }
  );

  const deleteSetting = () => {
    if (modalData?.modal === 'deleteSetting') {
      deleteSettingMutation({ settingId: modalData.settingId });
    }
  };

  const { mutate: createSettingMutation, isLoading: isCreateLoading } = useMutation(
    createChargeSetting,
    {
      onSuccess: () => {
        onCloseModal();
        queryCache.invalidateQueries(['charge-settings']);
        showAlert('Charge created', 'success');
      },
      onError: (errorResponse: any) => {
        const message = getMutationErrorMessage(
          errorResponse,
          'Unexpected error occurred while trying to create the setting'
        );

        if (message.includes('E11000')) {
          showAlert('Charge setting already exists', 'error');
        } else {
          showAlert(message, 'error');
        }
      },
    }
  );

  const { mutate: updateSettingMutation, isLoading: isUpdateLoading } = useMutation(
    updateChargeSetting,
    {
      onSuccess: () => {
        onCloseModal();
        queryCache.invalidateQueries(['charge-settings']);
        showAlert('Charge updated', 'success');
      },
      onError: (errorResponse: any) => {
        const message = getMutationErrorMessage(
          errorResponse,
          'Unexpected error occurred while trying to update the setting'
        );

        if (message.includes('E11000')) {
          showAlert('Charge setting already exists', 'error');
        } else {
          showAlert(message, 'error');
        }
      },
    }
  );

  return {
    isLoading,
    settingsPages,
    rowsPerPage,
    totalPages,
    totalRows,
    setCurrentPage,
    currentPage,
    changeRowsPerPage,
    modalData,
    setModalData,
    isDeleteLoading,
    onCloseModal,
    deleteSetting,
    createSettingMutation,
    updateSettingMutation,
    isCreateLoading,
    isUpdateLoading,
  };
};
